/**
 * Shared geometry for the Compose spread editor, the read-only preview,
 * the mini navigator, AND the PDF renderer. Everything that draws a
 * spread derives its numbers from here so the exported book is
 * geometrically identical to what the author composed on the canvas.
 *
 * Coordinate model: element x/y/w/h are percentages of the spread canvas
 * (interior spreads span BOTH facing pages; cover/back are single pages).
 * Text sizes are stored in design units — px at a SPREAD_DESIGN_W-wide
 * canvas — and scale linearly with the rendered canvas width.
 */
import {
  FORMAT_PRESETS,
  SPREAD_DESIGN_W,
  SPREAD_TEXT_PANEL_PAD,
} from '@historyjki/shared';
import type {
  FormatPreset,
  Spread,
  SpreadKind,
  SpreadElement,
  SpreadImageCrop,
  SpreadImageEdge,
  SpreadImageShape,
  SpreadImageShapeKind,
} from '@historyjki/shared';

/** Line height used for text elements everywhere (editor CSS + PDF).
 *  Canonical value lives in shared (the page-capacity estimate needs it);
 *  re-exported here for the existing editor/PDF imports. */
export { SPREAD_TEXT_LINE_HEIGHT } from '@historyjki/shared';

/** Element text colors (editor CSS custom props ↔ PDF rgb). */
export const SPREAD_TEXT_COLORS = {
  ink: { css: 'var(--ink-1)', rgb: [0.16, 0.14, 0.12] as const },
  paper: { css: 'var(--paper-0)', rgb: [0.985, 0.975, 0.955] as const },
};

/** 0–1 rgb triple → CSS rgba() string, so editor and PDF share exact colors. */
export function rgbaCss(rgb: readonly [number, number, number], alpha: number): string {
  const [r, g, b] = rgb.map((v) => Math.round(v * 255));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Text readability treatments (editor CSS ↔ PDF). All ratios are relative
 * to the rendered font size; pad/radius are spread design units (same
 * scale as element font sizes) so they track the canvas width.
 */
export const SPREAD_TEXT_EFFECTS = {
  /** Translucent panel behind the text box. The panel expands OUTWARD from
   *  the element box so text wrapping is untouched. */
  panel: {
    pad: SPREAD_TEXT_PANEL_PAD,
    radius: 8,
    byColor: {
      ink: { rgb: [0.985, 0.975, 0.955] as const, alpha: 0.78 },
      paper: { rgb: [0.15, 0.13, 0.11] as const, alpha: 0.5 },
    },
  },
  /** Hard 8-direction outline in the opposite ink — blur-free so the PDF
   *  (which cannot blur) renders the identical halo. */
  halo: {
    ratio: 0.06,
    byColor: {
      ink: { rgb: [0.985, 0.975, 0.955] as const },
      paper: { rgb: [0.16, 0.14, 0.12] as const },
    },
  },
  /** Single down-right offset shadow at partial opacity. */
  shadow: { ratio: 0.07, rgb: [0.1, 0.09, 0.08] as const, alpha: 0.5 },
};

/**
 * Text wrap around images (editor flow layout ↔ PDF). `standoff` is the
 * gap kept between glyphs and the image silhouette, in spread design
 * units (same scale as font sizes). `minSegmentEm` discards line
 * segments narrower than this many em — a channel that fits one or two
 * characters reads as a typo, not a wrap.
 */
export const SPREAD_TEXT_WRAP = {
  standoff: 14,
  minSegmentEm: 1.6,
};

/**
 * Image edge treatments (editor CSS ↔ PDF).
 *   rounded  — corner radius in spread design units.
 *   feather  — every edge fades over this % of the box's own axis.
 *   vignette — elliptical mask: radii as % of the box axes, opaque out to
 *              `inner` % of the radius, fading to transparent at 100%.
 *   torn     — deckled torn-paper outline: jagged points every `step`
 *              units of the box's short side, biting up to `amp` inward,
 *              softened by a tiny `blur`.
 *   brush    — loose painted-wash border: a smoothed blob inset by
 *              `inset` ± `amp`, with a wide `blur` for the wet edge.
 *   scallop  — cloud frame: an inset rectangle plus circles of radius
 *              `radius` marching along its perimeter.
 * torn/brush/scallop units are % of the box's SHORT side; all three are
 * rendered through the same deterministic SVG mask (edgeMaskSvg) so the
 * editor's CSS mask and the PDF's baked alpha are pixel-identical.
 * `rounded.shapeRadius` is the corner radius (in % of the box short side)
 * used when 'rounded' stacks on a cutout shape — the silhouette has no
 * canvas-relative frame corners, so it rounds relative to its own box.
 */
export const SPREAD_IMAGE_EDGES = {
  rounded: { radius: 22, shapeRadius: 6 },
  feather: { fadePct: 8 },
  vignette: { radiusPct: 72, innerPct: 55 },
  torn: { step: 7, amp: 4.5, blur: 0.5, seed: 7 },
  brush: { step: 16, inset: 6, amp: 4, blur: 2.4, seed: 11 },
  scallop: { inset: 8, radius: 7, blur: 0.6 },
};

/** Edge kinds rendered via the shared SVG mask (vs. plain CSS/vector). */
export type SvgMaskEdge = 'torn' | 'brush' | 'scallop';

/** Tiny deterministic PRNG (mulberry32) — identical jitter on every
 *  render and in the PDF bake, so nothing shimmers or drifts. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const fmt = (n: number) => String(Math.round(n * 100) / 100);

/**
 * Walk the perimeter of the `inset` rectangle inside a W×H viewBox,
 * emitting points every ~`step` units, each pushed further inward by
 * `jitter()` (0..amp). Returns the closed point ring, clockwise.
 */
function perimeterPoints(
  W: number,
  H: number,
  inset: number,
  step: number,
  jitter: () => number,
): Array<[number, number]> {
  const pts: Array<[number, number]> = [];
  const edges: Array<{
    from: [number, number];
    dir: [number, number];
    normal: [number, number];
    len: number;
  }> = [
    { from: [inset, inset], dir: [1, 0], normal: [0, 1], len: W - 2 * inset },
    { from: [W - inset, inset], dir: [0, 1], normal: [-1, 0], len: H - 2 * inset },
    { from: [W - inset, H - inset], dir: [-1, 0], normal: [0, -1], len: W - 2 * inset },
    { from: [inset, H - inset], dir: [0, -1], normal: [1, 0], len: H - 2 * inset },
  ];
  for (const e of edges) {
    const n = Math.max(2, Math.round(e.len / step));
    for (let i = 0; i < n; i++) {
      const t = (i / n) * e.len;
      const d = jitter();
      pts.push([
        e.from[0] + e.dir[0] * t + e.normal[0] * d,
        e.from[1] + e.dir[1] * t + e.normal[1] * d,
      ]);
    }
  }
  return pts;
}

/**
 * Build the deterministic SVG mask for a torn/brush/scallop edge. The
 * viewBox matches the element box's aspect ratio (quantized to 2 dp so
 * editor and exporter always agree), and the shape is drawn in units of
 * the box's short side. `pxWidth` sets explicit pixel dimensions on the
 * <svg> so canvas rasterization (the PDF bake) is crisp at output size.
 */
export function edgeMaskSvg(
  edge: SvgMaskEdge,
  boxAspect: number,
  pxWidth?: number,
): string {
  const aspect = Math.round(clampAspect(boxAspect) * 100) / 100;
  const W = 100;
  const H = 100 / aspect;
  const u = Math.min(W, H) / 100; // 1% of the short side
  let blur = 0;
  let shapes = '';

  if (edge === 'torn') {
    const p = SPREAD_IMAGE_EDGES.torn;
    const rnd = mulberry32(p.seed);
    const pts = perimeterPoints(W, H, 0.4 * u, p.step * u, () => rnd() * p.amp * u);
    shapes = `<polygon points="${pts.map(([x, y]) => `${fmt(x)},${fmt(y)}`).join(' ')}"/>`;
    blur = p.blur * u;
  } else if (edge === 'brush') {
    const p = SPREAD_IMAGE_EDGES.brush;
    const rnd = mulberry32(p.seed);
    const pts = perimeterPoints(W, H, p.inset * u, p.step * u, () => rnd() * p.amp * u);
    // Smooth blob: quadratic curves through consecutive midpoints.
    const mid = (a: [number, number], b: [number, number]): [number, number] => [
      (a[0] + b[0]) / 2,
      (a[1] + b[1]) / 2,
    ];
    const start = mid(pts[0], pts[1]);
    let d = `M ${fmt(start[0])} ${fmt(start[1])} `;
    for (let i = 0; i < pts.length; i++) {
      const ctrl = pts[(i + 1) % pts.length];
      const to = mid(ctrl, pts[(i + 2) % pts.length]);
      d += `Q ${fmt(ctrl[0])} ${fmt(ctrl[1])} ${fmt(to[0])} ${fmt(to[1])} `;
    }
    shapes = `<path d="${d}Z"/>`;
    blur = p.blur * u;
  } else {
    const p = SPREAD_IMAGE_EDGES.scallop;
    const inset = p.inset * u;
    const r = p.radius * u;
    const circles: string[] = [];
    const edgesDef: Array<{ from: [number, number]; dir: [number, number]; len: number }> = [
      { from: [inset, inset], dir: [1, 0], len: W - 2 * inset },
      { from: [W - inset, inset], dir: [0, 1], len: H - 2 * inset },
      { from: [W - inset, H - inset], dir: [-1, 0], len: W - 2 * inset },
      { from: [inset, H - inset], dir: [0, -1], len: H - 2 * inset },
    ];
    for (const e of edgesDef) {
      const n = Math.max(1, Math.round(e.len / (r * 1.5)));
      for (let i = 0; i <= n; i++) {
        const t = (i / n) * e.len;
        circles.push(
          `<circle cx="${fmt(e.from[0] + e.dir[0] * t)}" cy="${fmt(e.from[1] + e.dir[1] * t)}" r="${fmt(r)}"/>`,
        );
      }
    }
    shapes =
      `<rect x="${fmt(inset)}" y="${fmt(inset)}" width="${fmt(W - 2 * inset)}" height="${fmt(H - 2 * inset)}"/>` +
      circles.join('');
    blur = p.blur * u;
  }

  const pxH = pxWidth ? Math.max(1, Math.round(pxWidth / aspect)) : undefined;
  const sizeAttrs = pxWidth ? ` width="${pxWidth}" height="${pxH}"` : '';
  const filter =
    blur > 0
      ? `<filter id="s" x="-15%" y="-15%" width="130%" height="130%"><feGaussianBlur stdDeviation="${fmt(blur)}"/></filter>`
      : '';
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${fmt(W)} ${fmt(H)}"${sizeAttrs} preserveAspectRatio="none">` +
    `${filter}<g fill="#000"${filter ? ' filter="url(#s)"' : ''}>${shapes}</g></svg>`
  );
}

function clampAspect(a: number): number {
  return Number.isFinite(a) && a > 0 ? Math.max(0.2, Math.min(8, a)) : 1.4865;
}

const maskUriCache = new Map<string, string>();

/** `edgeMaskSvg` as a CSS-mask data URI, memoized per (edge, aspect). */
export function edgeMaskDataUri(edge: SvgMaskEdge, boxAspect: number): string {
  const key = `${edge}|${(Math.round(clampAspect(boxAspect) * 100) / 100).toFixed(2)}`;
  let uri = maskUriCache.get(key);
  if (!uri) {
    uri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(edgeMaskSvg(edge, boxAspect))}`;
    maskUriCache.set(key, uri);
  }
  return uri;
}

// ============================================================
// Cutout shapes — predefined silhouettes the author moves/rotates
// ============================================================

/** Default transform for a freshly picked cutout shape. */
export const DEFAULT_SHAPE_TRANSFORM = { x: 0, y: 0, rot: 0, scale: 1 } as const;

/** Clamp band for the shape transform (mirrored by the API sanitizer). */
export const SHAPE_OFFSET_LIMIT = 80;
export const SHAPE_SCALE_MIN = 0.25;
export const SHAPE_SCALE_MAX = 4;

/** Midpoint-smoothed closed path through a point ring (quadratic curves —
 *  same technique as the brush edge, but jitter-free/hand-tuned). */
function smoothClosedPath(pts: Array<[number, number]>): string {
  const mid = (a: [number, number], b: [number, number]): [number, number] => [
    (a[0] + b[0]) / 2,
    (a[1] + b[1]) / 2,
  ];
  const start = mid(pts[0], pts[1]);
  let d = `M ${fmt(start[0])} ${fmt(start[1])} `;
  for (let i = 0; i < pts.length; i++) {
    const ctrl = pts[(i + 1) % pts.length];
    const to = mid(ctrl, pts[(i + 2) % pts.length]);
    d += `Q ${fmt(ctrl[0])} ${fmt(ctrl[1])} ${fmt(to[0])} ${fmt(to[1])} `;
  }
  return `${d}Z`;
}

/**
 * Shape silhouettes in a unit space centered on the box: x and y run
 * -0.5..0.5 across the box's width/height, y down (CSS-like). Vertices are
 * hand-tuned so each cut reads at a glance in the picker AND leaves most
 * of the art visible at the default transform.
 */
const SHAPE_UNIT_POINTS: Record<
  Exclude<SpreadImageShapeKind, 'wave' | 'blob' | 'arch' | 'ellipse'>,
  Array<[number, number]>
> = {
  // One slanted edge — a diagonal page split.
  slant: [
    [-0.5, -0.3],
    [0.5, -0.5],
    [0.5, 0.5],
    [-0.5, 0.5],
  ],
  // Both sides leaning the same way — a dynamic comic panel.
  parallelogram: [
    [-0.32, -0.5],
    [0.5, -0.5],
    [0.32, 0.5],
    [-0.5, 0.5],
  ],
  // Irregular four-sided cut, like a shard of glass.
  shard: [
    [-0.5, -0.36],
    [0.1, -0.5],
    [0.5, 0.22],
    [-0.28, 0.5],
  ],
  // Sharp triangular sliver pointing right.
  wedge: [
    [-0.5, -0.46],
    [0.5, -0.04],
    [-0.5, 0.36],
  ],
};

/** Blob control ring (angle°, radius in half-box units) with alternating
 *  far/near radii so the midpoint smoothing keeps visible lobes instead
 *  of averaging into an oval. Shared by the mask path and the text-wrap
 *  outline sampler so both trace the same silhouette. */
const BLOB_RING: Array<[number, number]> = (
  [
    [354, 0.5],
    [38, 0.35],
    [74, 0.5],
    [112, 0.34],
    [148, 0.49],
    [188, 0.36],
    [222, 0.5],
    [262, 0.37],
    [298, 0.5],
    [330, 0.38],
  ] as Array<[number, number]>
).map(([deg, r]) => {
  const a = (deg * Math.PI) / 180;
  return [Math.cos(a) * r, Math.sin(a) * r];
});

/**
 * Inner SVG markup for a cutout silhouette, centered on the origin and
 * sized in box units (a W×H box). No fill/stroke/transform — callers wrap
 * it in a <g> that paints (mask) or outlines (adjust overlay) it.
 */
export function shapeCutMarkup(kind: SpreadImageShapeKind, W: number, H: number): string {
  const px = (p: [number, number]) => `${fmt(p[0] * W)},${fmt(p[1] * H)}`;
  if (kind === 'ellipse') {
    return `<ellipse cx="0" cy="0" rx="${fmt(W / 2)}" ry="${fmt(H / 2)}"/>`;
  }
  if (kind === 'arch') {
    // Flat-bottomed arch: straight sides up to mid-height, elliptical top.
    const d =
      `M ${fmt(-W / 2)} ${fmt(H / 2)} L ${fmt(-W / 2)} 0 ` +
      `A ${fmt(W / 2)} ${fmt(H / 2)} 0 0 1 ${fmt(W / 2)} 0 ` +
      `L ${fmt(W / 2)} ${fmt(H / 2)} Z`;
    return `<path d="${d}"/>`;
  }
  if (kind === 'wave') {
    // Rolling crest-and-trough top edge over a solid panel.
    const d =
      `M ${fmt(-0.5 * W)} ${fmt(-0.2 * H)} ` +
      `Q ${fmt(-0.25 * W)} ${fmt(-0.44 * H)} 0 ${fmt(-0.2 * H)} ` +
      `Q ${fmt(0.25 * W)} ${fmt(0.04 * H)} ${fmt(0.5 * W)} ${fmt(-0.2 * H)} ` +
      `L ${fmt(0.5 * W)} ${fmt(0.5 * H)} L ${fmt(-0.5 * W)} ${fmt(0.5 * H)} Z`;
    return `<path d="${d}"/>`;
  }
  if (kind === 'blob') {
    return `<path d="${smoothClosedPath(BLOB_RING.map(([x, y]) => [x * W, y * H]))}"/>`;
  }
  const pts = SHAPE_UNIT_POINTS[kind];
  return `<polygon points="${pts.map(px).join(' ')}"/>`;
}

/**
 * SVG transform placing a shape inside a W×H box: offset the center by
 * x/y (percent of the box's own axes), spin by rot degrees, scale
 * uniformly. The viewBox is aspect-true (W/H = box aspect), so the
 * rotation renders rigid — no shear.
 */
export function shapeTransform(shape: SpreadImageShape, W: number, H: number): string {
  const cx = W / 2 + (shape.x / 100) * W;
  const cy = H / 2 + (shape.y / 100) * H;
  const parts = [`translate(${fmt(cx)} ${fmt(cy)})`];
  if (shape.rot) parts.push(`rotate(${fmt(shape.rot)})`);
  if (shape.scale !== 1) parts.push(`scale(${fmt(shape.scale)})`);
  return parts.join(' ');
}

/**
 * Aspect-true viewBox for a shape mask/outline over a box: W is fixed at
 * 100 and H derived from the (quantized) box aspect, exactly like
 * edgeMaskSvg, so rotations render rigid and editor/exporter agree.
 */
export function shapeMaskViewBox(boxAspect: number): { W: number; H: number } {
  const aspect = Math.round(clampAspect(boxAspect) * 100) / 100;
  return { W: 100, H: 100 / aspect };
}

/**
 * Deterministic SVG mask for a cutout shape — the shape sibling of
 * `edgeMaskSvg`, sharing its viewBox conventions so the editor's CSS mask
 * and the PDF's baked alpha stay pixel-identical. `pxWidth` sets explicit
 * pixel dimensions for crisp canvas rasterization at bake time.
 */
export function shapeMaskSvg(
  shape: SpreadImageShape,
  boxAspect: number,
  pxWidth?: number,
): string {
  const { W, H } = shapeMaskViewBox(boxAspect);
  const pxH = pxWidth ? Math.max(1, Math.round((pxWidth * H) / W)) : undefined;
  const sizeAttrs = pxWidth ? ` width="${pxWidth}" height="${pxH}"` : '';
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${fmt(W)} ${fmt(H)}"${sizeAttrs} preserveAspectRatio="none">` +
    `<g fill="#000" transform="${shapeTransform(shape, W, H)}">${shapeCutMarkup(shape.kind, W, H)}</g></svg>`
  );
}

/**
 * Outline of a cutout shape under the author's transform, CLIPPED to the
 * box, as a closed ring in viewBox units (0..W / 0..H, y down). This is
 * the VISIBLE silhouette: the mask only ever reveals image inside the
 * box, so parts pushed past the box cut nothing. A shape moved fully
 * outside comes back empty. On a concave ring the clip can leave
 * degenerate edges along the box border — harmless for both consumers
 * (edge treatments walk the ring; text wrap reads x-extents per band).
 */
function shapeVisibleRing(
  shape: SpreadImageShape,
  W: number,
  H: number,
): Array<[number, number]> {
  const cx = W / 2 + (shape.x / 100) * W;
  const cy = H / 2 + (shape.y / 100) * H;
  const rad = (shape.rot * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const s = shape.scale;
  // Same order as shapeTransform's SVG list (translate·rotate·scale):
  // scale the design-space point, spin it, then move it to the center.
  let ring: Array<[number, number]> = shapeOutlinePoints(shape.kind, W, H).map(
    ([px, py]) => {
      const sx = px * s;
      const sy = py * s;
      return [cx + sx * cos - sy * sin, cy + sx * sin + sy * cos];
    },
  );
  ring = clipRingHalfPlane(ring, 0, 0, false);
  ring = clipRingHalfPlane(ring, 0, W, true);
  ring = clipRingHalfPlane(ring, 1, 0, false);
  ring = clipRingHalfPlane(ring, 1, H, true);
  return ring;
}

/** Resample a closed ring at (near-)uniform arc-length spacing. */
function resampleRing(
  ring: Array<[number, number]>,
  spacing: number,
): Array<[number, number]> {
  if (ring.length < 3) return [];
  const n = ring.length;
  const segLens: number[] = [];
  let total = 0;
  for (let i = 0; i < n; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % n];
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    segLens.push(len);
    total += len;
  }
  if (total <= 0) return [];
  const count = Math.max(8, Math.round(total / spacing));
  const step = total / count;
  const out: Array<[number, number]> = [];
  let seg = 0;
  let segStart = 0;
  for (let k = 0; k < count; k++) {
    const target = k * step;
    while (seg < n - 1 && segStart + segLens[seg] < target) {
      segStart += segLens[seg];
      seg += 1;
    }
    const len = segLens[seg];
    const t = len > 0 ? (target - segStart) / len : 0;
    const a = ring[seg];
    const b = ring[(seg + 1) % n];
    out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
  }
  return out;
}

/** Unit normals pointing into the ring's interior, one per point
 *  (neighbor tangents; the shoelace sign picks the inward side). */
function inwardNormals(ring: Array<[number, number]>): Array<[number, number]> {
  const n = ring.length;
  let area2 = 0;
  for (let i = 0; i < n; i++) {
    const [ax, ay] = ring[i];
    const [bx, by] = ring[(i + 1) % n];
    area2 += ax * by - bx * ay;
  }
  const sign = area2 >= 0 ? 1 : -1;
  return ring.map((_, i) => {
    const p = ring[(i - 1 + n) % n];
    const q = ring[(i + 1) % n];
    const tx = q[0] - p[0];
    const ty = q[1] - p[1];
    const len = Math.hypot(tx, ty) || 1;
    return [(-ty / len) * sign, (tx / len) * sign];
  });
}

/** Every ring point pushed `dist` along its inward normal. */
function offsetRingInward(
  ring: Array<[number, number]>,
  dist: number,
): Array<[number, number]> {
  const normals = inwardNormals(ring);
  return ring.map(([x, y], i) => [x + normals[i][0] * dist, y + normals[i][1] * dist]);
}

const ringPolygon = (pts: Array<[number, number]>) =>
  `<polygon points="${pts.map(([x, y]) => `${fmt(x)},${fmt(y)}`).join(' ')}"/>`;

/**
 * Deterministic SVG mask for a cutout shape WITH an edge treatment
 * stacked on it. The softening applies to the resulting cut — the
 * visible silhouette (shape ∩ box) — not to the element rectangle.
 * Shares shapeMaskSvg's viewBox conventions, and the editor's CSS mask
 * and the PDF bake rasterize the identical string, so both stay
 * pixel-identical.
 *   rounded  — gaussian blur + steep alpha ramp re-hardens the outline,
 *              rounding its corners (radius: rounded.shapeRadius % of
 *              the box short side),
 *   feather  — the silhouette, pulled half a fade inward, blurred so
 *              its edge melts into the page,
 *   vignette — the silhouette filled with the same elliptical gradient
 *              as the rectangle vignette, anchored in the shape's own
 *              frame so it moves/rotates/scales with the cutout,
 *   torn/brush/scallop — the rectangle recipes generalized: the
 *              perimeter walk runs along the visible silhouette instead
 *              of the box perimeter (same steps, jitter amplitudes,
 *              seeds, and blurs as edgeMaskSvg).
 */
export function shapeEdgeMaskSvg(
  shape: SpreadImageShape,
  edge: SpreadImageEdge,
  boxAspect: number,
  pxWidth?: number,
): string {
  if (edge === 'sharp') return shapeMaskSvg(shape, boxAspect, pxWidth);
  const { W, H } = shapeMaskViewBox(boxAspect);
  const u = Math.min(W, H) / 100; // 1% of the short side
  const pxH = pxWidth ? Math.max(1, Math.round((pxWidth * H) / W)) : undefined;
  const sizeAttrs = pxWidth ? ` width="${pxWidth}" height="${pxH}"` : '';
  const open = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${fmt(W)} ${fmt(
    H,
  )}"${sizeAttrs} preserveAspectRatio="none">`;

  if (edge === 'rounded') {
    // "Goo" rounding: blur by the radius, then a steep alpha ramp
    // (centered on 0.5) restores a hard edge — straight runs stay put,
    // corners round with radius ≈ the blur. The filter sits OUTSIDE the
    // shape transform so the radius is in viewBox units, independent of
    // the author's shape scale.
    const r = SPREAD_IMAGE_EDGES.rounded.shapeRadius * u;
    const filter =
      `<filter id="s" x="-15%" y="-15%" width="130%" height="130%">` +
      `<feGaussianBlur stdDeviation="${fmt(r)}"/>` +
      `<feComponentTransfer><feFuncA type="linear" slope="14" intercept="-6.5"/></feComponentTransfer>` +
      `</filter>`;
    return (
      `${open}${filter}<g fill="#000" filter="url(#s)">` +
      `<g transform="${shapeTransform(shape, W, H)}">${shapeCutMarkup(shape.kind, W, H)}</g></g></svg>`
    );
  }

  if (edge === 'vignette') {
    // The rectangle vignette's ellipse (radiusPct of each box axis,
    // opaque to innerPct of the radius), expressed in the shape's local
    // design space — the group transform then carries it with the
    // cutout's move/rotate/scale. Painting the silhouette WITH the
    // gradient intersects the two, like the CSS mask stack would.
    const v = SPREAD_IMAGE_EDGES.vignette;
    const grad =
      `<radialGradient id="vg" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" ` +
      `gradientTransform="scale(${fmt((v.radiusPct / 100) * W)} ${fmt((v.radiusPct / 100) * H)})">` +
      `<stop offset="0" stop-color="#000"/>` +
      `<stop offset="${fmt(v.innerPct / 100)}" stop-color="#000"/>` +
      `<stop offset="1" stop-color="#000" stop-opacity="0"/>` +
      `</radialGradient>`;
    return (
      `${open}${grad}<g fill="url(#vg)" transform="${shapeTransform(shape, W, H)}">` +
      `${shapeCutMarkup(shape.kind, W, H)}</g></svg>`
    );
  }

  // Ring-walking treatments follow the visible silhouette.
  const ring = shapeVisibleRing(shape, W, H);
  let blur = 0;
  let shapes = '';
  if (ring.length >= 3) {
    if (edge === 'feather') {
      // Fade depth matches the rectangle feather's fadePct, taken of the
      // short side. Pulling the ring half a fade inward before blurring
      // keeps the melt essentially inside the silhouette (the residual
      // tail past the true outline is ~2% alpha).
      const fade = SPREAD_IMAGE_EDGES.feather.fadePct * u;
      const pts = offsetRingInward(resampleRing(ring, 2 * u), fade / 2);
      if (pts.length >= 3) shapes = ringPolygon(pts);
      blur = fade / 4;
    } else if (edge === 'torn') {
      const p = SPREAD_IMAGE_EDGES.torn;
      const rnd = mulberry32(p.seed);
      const base = resampleRing(ring, p.step * u);
      const normals = inwardNormals(base);
      const pts = base.map(([x, y], i): [number, number] => {
        const d = 0.4 * u + rnd() * p.amp * u;
        return [x + normals[i][0] * d, y + normals[i][1] * d];
      });
      if (pts.length >= 3) shapes = ringPolygon(pts);
      blur = p.blur * u;
    } else if (edge === 'brush') {
      const p = SPREAD_IMAGE_EDGES.brush;
      const rnd = mulberry32(p.seed);
      const base = resampleRing(ring, p.step * u);
      const normals = inwardNormals(base);
      const pts = base.map(([x, y], i): [number, number] => {
        const d = (p.inset + rnd() * p.amp) * u;
        return [x + normals[i][0] * d, y + normals[i][1] * d];
      });
      if (pts.length >= 3) shapes = `<path d="${smoothClosedPath(pts)}"/>`;
      blur = p.blur * u;
    } else {
      // scallop
      const p = SPREAD_IMAGE_EDGES.scallop;
      const r = p.radius * u;
      const insetRing = offsetRingInward(resampleRing(ring, 2 * u), p.inset * u);
      if (insetRing.length >= 3) {
        const centers = resampleRing(insetRing, r * 1.5);
        shapes =
          ringPolygon(insetRing) +
          centers
            .map(([x, y]) => `<circle cx="${fmt(x)}" cy="${fmt(y)}" r="${fmt(r)}"/>`)
            .join('');
      }
      blur = p.blur * u;
    }
  }
  const filter =
    blur > 0 && shapes
      ? `<filter id="s" x="-15%" y="-15%" width="130%" height="130%"><feGaussianBlur stdDeviation="${fmt(blur)}"/></filter>`
      : '';
  return `${open}${filter}<g fill="#000"${filter ? ' filter="url(#s)"' : ''}>${shapes}</g></svg>`;
}

/** `shapeEdgeMaskSvg` as a CSS-mask data URI, memoized per
 *  (shape, edge, aspect). Dragging the adjust overlay generates many
 *  keys — cap the cache. */
export function shapeMaskDataUri(
  shape: SpreadImageShape,
  boxAspect: number,
  edge: SpreadImageEdge = 'sharp',
): string {
  const key = `shape|${shape.kind}|${shape.x}|${shape.y}|${shape.rot}|${shape.scale}|${edge}|${(
    Math.round(clampAspect(boxAspect) * 100) / 100
  ).toFixed(2)}`;
  let uri = maskUriCache.get(key);
  if (!uri) {
    if (maskUriCache.size > 500) maskUriCache.clear();
    uri = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
      shapeEdgeMaskSvg(shape, edge, boxAspect),
    )}`;
    maskUriCache.set(key, uri);
  }
  return uri;
}

// ============================================================
// Text wrap — silhouette outlines for the flow layout
// ============================================================

/** Sample a quadratic bezier from `from` via `ctrl` to `to`, excluding the
 *  final point (rings chain segments, so `to` opens the next one). */
function sampleQuad(
  from: [number, number],
  ctrl: [number, number],
  to: [number, number],
  steps: number,
): Array<[number, number]> {
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < steps; i++) {
    const t = i / steps;
    const a = (1 - t) * (1 - t);
    const b = 2 * (1 - t) * t;
    const c = t * t;
    pts.push([
      a * from[0] + b * ctrl[0] + c * to[0],
      a * from[1] + b * ctrl[1] + c * to[1],
    ]);
  }
  return pts;
}

/** Sampled ring of the midpoint-smoothed closed path through `pts` — the
 *  polyline twin of smoothClosedPath, for geometry instead of paint. */
function sampleSmoothClosed(
  pts: Array<[number, number]>,
  perSegment: number,
): Array<[number, number]> {
  const mid = (a: [number, number], b: [number, number]): [number, number] => [
    (a[0] + b[0]) / 2,
    (a[1] + b[1]) / 2,
  ];
  const ring: Array<[number, number]> = [];
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const from = mid(pts[i], pts[(i + 1) % n]);
    const ctrl = pts[(i + 1) % n];
    const to = mid(pts[(i + 1) % n], pts[(i + 2) % n]);
    ring.push(...sampleQuad(from, ctrl, to, perSegment));
  }
  return ring;
}

/**
 * Closed outline ring of a cutout silhouette, centered on the origin and
 * sized in box units (a W×H box) — the same space as shapeCutMarkup, with
 * curved kinds flattened to polylines dense enough for text-wrap
 * intervals (glyph-height bands only ever need the ring's x-extent).
 */
export function shapeOutlinePoints(
  kind: SpreadImageShapeKind,
  W: number,
  H: number,
): Array<[number, number]> {
  if (kind === 'ellipse') {
    const pts: Array<[number, number]> = [];
    for (let i = 0; i < 32; i++) {
      const a = (i / 32) * 2 * Math.PI;
      pts.push([(Math.cos(a) * W) / 2, (Math.sin(a) * H) / 2]);
    }
    return pts;
  }
  if (kind === 'arch') {
    // Flat-bottomed arch: straight sides up to mid-height, elliptical top.
    const pts: Array<[number, number]> = [
      [-W / 2, H / 2],
      [-W / 2, 0],
    ];
    for (let i = 1; i < 16; i++) {
      const t = (i / 16) * Math.PI;
      pts.push([(-Math.cos(t) * W) / 2, (-Math.sin(t) * H) / 2]);
    }
    pts.push([W / 2, 0], [W / 2, H / 2]);
    return pts;
  }
  if (kind === 'wave') {
    // The same two crest/trough quadratics as the mask path.
    const p0: [number, number] = [-0.5 * W, -0.2 * H];
    const p1: [number, number] = [0, -0.2 * H];
    const p2: [number, number] = [0.5 * W, -0.2 * H];
    return [
      ...sampleQuad(p0, [-0.25 * W, -0.44 * H], p1, 8),
      ...sampleQuad(p1, [0.25 * W, 0.04 * H], p2, 8),
      p2,
      [0.5 * W, 0.5 * H],
      [-0.5 * W, 0.5 * H],
    ];
  }
  if (kind === 'blob') {
    return sampleSmoothClosed(
      BLOB_RING.map(([x, y]) => [x * W, y * H]),
      4,
    );
  }
  const pts = SHAPE_UNIT_POINTS[kind];
  return pts.map(([x, y]) => [x * W, y * H]);
}

/** One Sutherland–Hodgman pass: keep the part of a closed ring on one
 *  side of an axis-aligned line (`p[axis] ≤ limit` when `keepBelow`,
 *  `≥ limit` otherwise), inserting the crossing points. May return fewer
 *  than 3 points (or none) when the ring barely reaches — or misses —
 *  the half-plane. */
function clipRingHalfPlane(
  ring: Array<[number, number]>,
  axis: 0 | 1,
  limit: number,
  keepBelow: boolean,
): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  const inside = (p: [number, number]) =>
    keepBelow ? p[axis] <= limit : p[axis] >= limit;
  const n = ring.length;
  for (let i = 0; i < n; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % n];
    if (inside(a)) out.push(a);
    if (inside(a) !== inside(b)) {
      const t = (limit - a[axis]) / (b[axis] - a[axis]);
      out.push([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]);
    }
  }
  return out;
}

/**
 * Outline of a placed image's visible silhouette as a closed ring in
 * BOX-FRACTION coordinates (0..1 across the element box's width/height,
 * y down). A cutout shape is sampled under the author's current
 * move/rotate/scale — the same transform shapeMaskSvg bakes — then
 * CLIPPED to the element box: the mask only ever reveals image inside
 * the box, so the parts of the shape the author pushed past the box cut
 * nothing and must not repel text. A shape moved fully outside comes
 * back empty (nothing visible, nothing to wrap). Without a shape the
 * ring is the plain box: edge softening (torn/feather/…) textures the
 * rectangle but doesn't change the composition text should respect.
 */
export function imageWrapOutline(
  el: { shape?: SpreadImageShape | null },
  boxAspect: number,
): Array<[number, number]> {
  if (!el.shape) {
    return [
      [0, 0],
      [1, 0],
      [1, 1],
      [0, 1],
    ];
  }
  const { W, H } = shapeMaskViewBox(boxAspect);
  // The clipped visible silhouette (shape ∩ box), scaled from viewBox
  // units to box fractions.
  return shapeVisibleRing(el.shape, W, H).map(([x, y]) => [x / W, y / H]);
}

/**
 * Exclusion outlines a text element's flow layout must avoid: the
 * silhouette ring of every text_wrap image that comes near the text box,
 * in spread DESIGN UNITS relative to the text box's top-left corner (the
 * space flowText lays lines out in — font sizes are already design
 * units, and design space is aspect-true so rotated rings stay rigid).
 * Order/stacking is ignored on purpose: wrap is a per-image author
 * choice, not a z-order accident.
 */
export function computeWrapExclusions(
  textEl: { x: number; y: number; w: number; h: number },
  elements: SpreadElement[],
  canvasAspect: number,
): Array<Array<[number, number]>> {
  const designW = SPREAD_DESIGN_W;
  const designH = designW / canvasAspect;
  const tx = (textEl.x / 100) * designW;
  const ty = (textEl.y / 100) * designH;
  const tw = (textEl.w / 100) * designW;
  const th = (textEl.h / 100) * designH;
  const reach = SPREAD_TEXT_WRAP.standoff;

  const out: Array<Array<[number, number]>> = [];
  for (const el of elements) {
    if (el.type !== 'image' || !el.text_wrap) continue;
    const boxAspect = (el.w / el.h) * canvasAspect;
    const ring = imageWrapOutline(el, boxAspect).map(([fx, fy]): [number, number] => [
      ((el.x + fx * el.w) / 100) * designW - tx,
      ((el.y + fy * el.h) / 100) * designH - ty,
    ]);
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const [x, y] of ring) {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    // Keep only rings whose standoff-inflated bounds touch the text box.
    if (minX < tw + reach && maxX > -reach && minY < th + reach && maxY > -reach) {
      out.push(ring);
    }
  }
  return out;
}

/** Physical page size in points (1 in = 72 pt) for a format preset. */
export function getPageSizePt(formatPreset: FormatPreset | null | undefined): {
  widthPt: number;
  heightPt: number;
} {
  const preset = FORMAT_PRESETS.find((p) => p.value === formatPreset);
  const width = preset?.width ?? 8;
  const height = preset?.height ?? 8;
  return { widthPt: width * 72, heightPt: height * 72 };
}

/**
 * Canvas size in points for a spread kind: interior spreads are two
 * facing pages wide; cover/back are a single page.
 */
export function getCanvasSizePt(
  formatPreset: FormatPreset | null | undefined,
  kind: SpreadKind,
): { widthPt: number; heightPt: number } {
  const { widthPt, heightPt } = getPageSizePt(formatPreset);
  return kind === 'spread'
    ? { widthPt: widthPt * 2, heightPt }
    : { widthPt, heightPt };
}

/** Unitless width/height ratio for the editor's `--spread-ar` CSS var. */
export function getCanvasAspect(
  formatPreset: FormatPreset | null | undefined,
  kind: SpreadKind,
): number {
  const { widthPt, heightPt } = getCanvasSizePt(formatPreset, kind);
  return widthPt / heightPt;
}

/**
 * Font size for a text element rendered onto a canvas of the given width.
 * Mirrors the editor CSS rule
 *   font-size: calc(var(--els) * (100 / SPREAD_DESIGN_W) cqw)
 * i.e. sizes scale with the canvas width relative to the design canvas.
 */
export function textSizeForCanvasWidth(
  designSize: number,
  canvasWidth: number,
): number {
  return (designSize / SPREAD_DESIGN_W) * canvasWidth;
}

/** Defensive parse of a spread's elements JSON. */
export function parseSpreadElements(raw: string | null): SpreadElement[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is SpreadElement =>
        !!e &&
        typeof e === 'object' &&
        ((e as SpreadElement).type === 'image' || (e as SpreadElement).type === 'text'),
    );
  } catch {
    return [];
  }
}

/** Elements sorted bottom-to-top for painting. */
export function sortedElements(elements: SpreadElement[]): SpreadElement[] {
  return [...elements].sort((a, b) => a.z - b.z);
}

/** Human label for a spread in pager / preview / toolbar. */
export function spreadLabel(
  spread: Pick<Spread, 'kind'>,
  interiorIndex: number,
): { label: string; pages: string | null } {
  if (spread.kind === 'cover') return { label: 'Cover', pages: null };
  if (spread.kind === 'back') return { label: 'Back', pages: null };
  const left = 2 + interiorIndex * 2;
  return { label: `Spread ${interiorIndex + 1}`, pages: `${left}–${left + 1}` };
}

/**
 * "Cover-fit" placement of an image inside a box (the editor renders
 * placed images with object-fit: cover). Returns the draw rect — larger
 * than the box on one axis — which callers clip to the box.
 */
export function coverFitRect(
  box: { x: number; y: number; width: number; height: number },
  imageWidth: number,
  imageHeight: number,
): { x: number; y: number; width: number; height: number } {
  if (imageWidth <= 0 || imageHeight <= 0) return { ...box };
  const scale = Math.max(box.width / imageWidth, box.height / imageHeight);
  const width = imageWidth * scale;
  const height = imageHeight * scale;
  return {
    x: box.x - (width - box.width) / 2,
    y: box.y - (height - box.height) / 2,
    width,
    height,
  };
}

/**
 * Cover-fit generalized to a crop: the CROP REGION of the image (percent
 * of the source, or the whole image when absent) is scaled to cover the
 * box with its center at the box center. Returns the draw rect of the
 * FULL image — callers clip to the box — in the same top-down coordinate
 * space as `box` (CSS-like; PDF callers flip the y themselves).
 */
export function cropCoverRect(
  box: { x: number; y: number; width: number; height: number },
  imageWidth: number,
  imageHeight: number,
  crop?: SpreadImageCrop | null,
): { x: number; y: number; width: number; height: number } {
  if (!crop) return coverFitRect(box, imageWidth, imageHeight);
  if (imageWidth <= 0 || imageHeight <= 0) return { ...box };
  const cx = (crop.x / 100) * imageWidth;
  const cy = (crop.y / 100) * imageHeight;
  const cw = (crop.w / 100) * imageWidth;
  const ch = (crop.h / 100) * imageHeight;
  if (cw <= 0 || ch <= 0) return coverFitRect(box, imageWidth, imageHeight);
  const scale = Math.max(box.width / cw, box.height / ch);
  return {
    x: box.x + box.width / 2 - (cx + cw / 2) * scale,
    y: box.y + box.height / 2 - (cy + ch / 2) * scale,
    width: imageWidth * scale,
    height: imageHeight * scale,
  };
}
